import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { mediciAPI } from '../services/api';

/**
 * MedicoSelect - dropdown con ricerca per selezionare un medico.
 *
 * Props:
 *  - value        (number|null)        ID del medico selezionato (controlled)
 *  - onChange     (fn)                 callback (medicoId, medicoObj) => void
 *  - name         (string)             nome dell'input nascosto per FormData (default: 'medico_id')
 *  - medici       (array)              lista medici già caricata (opzionale, evita una chiamata API)
 *  - placeholder  (string)             testo placeholder
 *  - isClearable  (boolean)            mostra X per resettare (default: true)
 *  - soloAttivi   (boolean)            carica solo medici attivi se medici non fornito (default: true)
 *  - disabled     (boolean)
 */
const MedicoSelect = ({
  value,
  onChange,
  name = 'medico_id',
  medici: externalMedici,
  placeholder = 'Cerca medico per nome, cognome o specializzazione...',
  isClearable = true,
  soloAttivi = true,
  disabled = false,
}) => {
  const [internalMedici, setInternalMedici] = useState([]);
  const [loading, setLoading] = useState(false);

  // Carica medici internamente se non sono stati passati come prop
  useEffect(() => {
    if (externalMedici) return;
    const loadMedici = async () => {
      try {
        setLoading(true);
        const response = await mediciAPI.getAll(soloAttivi);
        if (response.data.success) {
          setInternalMedici(response.data.data.medici || []);
        }
      } catch (err) {
        console.error('Errore caricamento medici in MedicoSelect:', err);
      } finally {
        setLoading(false);
      }
    };
    loadMedici();
  }, [externalMedici, soloAttivi]);

  const medici = externalMedici || internalMedici;

  // Costruzione opzioni
  const options = medici.map((m) => ({
    value: m.id,
    label: `Dr. ${m.cognome} ${m.nome}${m.specializzazione ? ` — ${m.specializzazione}` : ''}`,
    medico: m,
  }));

  // Opzione selezionata (confronto con cast a Number per sicurezza)
  const selectedOption =
    value != null && value !== ''
      ? options.find((o) => String(o.value) === String(value)) || null
      : null;

  const handleChange = (option) => {
    const newValue = option ? option.value : null;
    if (onChange) {
      onChange(newValue, option?.medico || null);
    }
  };

  // Filtro custom: ricerca substring su più campi
  const filterOption = (option, inputValue) => {
    if (!inputValue) return true;
    const m = option.data.medico;
    const search = inputValue.toLowerCase().trim();
    return (
      (m.nome || '').toLowerCase().includes(search) ||
      (m.cognome || '').toLowerCase().includes(search) ||
      (m.specializzazione || '').toLowerCase().includes(search) ||
      /* Anche l'elenco: cercare «fisioterapia» deve trovare chi e'
         fisioterapista, anche se l'etichetta di stampa dice altro. */
      (m.specializzazioni || []).some((x) => (x || '').toLowerCase().includes(search)) ||
      (m.codice_fiscale || '').toLowerCase().includes(search) ||
      (m.numero_ordine || '').toString().toLowerCase().includes(search)
    );
  };

  // Stili per matchare l'aspetto degli altri input del progetto
  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '42px',
      borderRadius: '8px',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.12)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9ca3af',
      fontSize: '0.95rem',
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '0.95rem',
      backgroundColor: state.isSelected
        ? '#3b82f6'
        : state.isFocused
        ? '#eff6ff'
        : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      cursor: 'pointer',
      padding: '10px 12px',
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '8px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
      zIndex: 9999,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    singleValue: (base) => ({
      ...base,
      color: '#1f2937',
      fontSize: '0.95rem',
    }),
    input: (base) => ({
      ...base,
      fontSize: '0.95rem',
    }),
  };

  return (
    <>
      <Select
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={loading ? 'Caricamento medici...' : placeholder}
        isClearable={isClearable}
        isDisabled={disabled || loading}
        isLoading={loading}
        filterOption={filterOption}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? `Nessun medico trovato per "${inputValue}"` : 'Nessun medico disponibile'
        }
        loadingMessage={() => 'Caricamento...'}
        styles={customStyles}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
      />
      {/* Hidden input per compatibilità FormData */}
      <input type="hidden" name={name} value={selectedOption?.value || ''} />
    </>
  );
};

export default MedicoSelect;
